import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { isConciergeManualMode } from '../components/operations/manualModeFlag';

const ConciergeSubmittedPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [token, setToken] = useState('');
  const [eventId, setEventId] = useState('');
  const [expectationWindow, setExpectationWindow] = useState('1 week');

  useEffect(() => {
    const t = params.get('token') || (() => {
      try { return localStorage.getItem('celey:concierge:token') || ''; } catch (_e) { return ''; }
    })();
    const e = params.get('event') || (() => {
      try { return localStorage.getItem('celey:concierge:event_id') || ''; } catch (_e) { return ''; }
    })();
    const w = params.get('window') || '1 week';
    setToken(t);
    setEventId(e);
    setExpectationWindow(w);
  }, [params]);

  // Phase A — Manual Mode gate (ADR-CONCIERGE-PHASE-A). Read once per
  // render; defaults to TRUE so misconfiguration cannot accidentally
  // expose Hybrid-Mode "Brief" / overspecific-timing language.
  const manualMode = isConciergeManualMode();

  // Empty state: someone hit /concierge/submitted directly without a token.
  if (!token && !eventId) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <Sparkles className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-gray-900">No active submission</h2>
            <p className="text-sm text-gray-600 mt-2">
              We don&apos;t have a Concierge submission for you yet. Start one here.
            </p>
            <Button
              className="mt-4 bg-emerald-600 hover:bg-emerald-700"
              onClick={() => navigate('/concierge/intake')}
              data-testid="concierge-submitted-start-cta"
            >
              Submit a request <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/40 to-white py-12 px-4 sm:py-16">
      <div className="max-w-xl mx-auto">
        <Card className="border-emerald-100">
          <CardContent className="p-6 sm:p-8 text-center" data-testid="concierge-submitted-card">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 mb-4">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900" data-testid="concierge-submitted-title">
              Got it — we&apos;re on it.
            </h1>
            {!manualMode && (
              <p className="text-gray-600 mt-3 max-w-md mx-auto" data-testid="concierge-submitted-subhead">
                Your Concierge Brief will land in your inbox within{' '}
                <span className="font-medium text-gray-900">about {expectationWindow}</span> for standard requests.
                No need to do anything else right now.
              </p>
            )}

            {/* Phase A — Manual Mode 3-step expectation block.
                Hidden in Hybrid Mode where the subhead's specific
                timing window already sets expectations. */}
            {manualMode && (
              <div
                className="mt-8 text-left rounded-2xl bg-emerald-50/60 border border-emerald-100 p-4 sm:p-5 space-y-3"
                data-testid="concierge-submitted-expectations"
              >
                <p className="text-xs uppercase tracking-wide text-emerald-700 font-medium">
                  What happens next
                </p>

                <div className="flex items-start gap-3" data-testid="concierge-submitted-step-1">
                  <div className="shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold">1</div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 leading-snug">We&apos;ve received your event</p>
                    <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                      Your Concierge is reading through what you shared right now.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-testid="concierge-submitted-step-2">
                  <div className="shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold">2</div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 leading-snug">You&apos;ll hear from us by email</p>
                    <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                      Watch your inbox — we&apos;ll confirm the details and ask any clarifying questions. (Check your spam folder just in case.)
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-testid="concierge-submitted-step-3">
                  <div className="shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold">3</div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 leading-snug">We prepare your recommendations</p>
                    <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                      We'll research options and send a curated Concierge Brief when it's ready.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Defect 6 (B-EXP-6.18): removed misleading "Save your link"
                block. The submitted-page URL only restores this receipt
                view — it is NOT a persistent Event Home or Concierge
                tracking portal. Customers reach their real brief through
                the staff-driven "Send to customer" publish flow, which
                delivers a distinct share link by email. */}

            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                data-testid="concierge-submitted-home"
              >
                Back to home
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => {
                  // Phase 8.6 — signal to CustomerDashboard that the
                  // next mount should redirect straight to the adopted
                  // event's home if the customer has exactly one
                  // pre-graduation Concierge event. Cleared on first
                  // dashboard mount.
                  try {
                    sessionStorage.setItem('celey_concierge_just_signed_up', '1');
                  } catch (_) { /* ignore quota / privacy errors */ }
                  // B-SEC-6.25 — forward BOTH the intake email (for
                  // prefill UX) AND the share-token (authoritative
                  // adoption proof). The token is what unlocks the
                  // server-side claim; the email is convenience only.
                  let prefillEmail = '';
                  try {
                    prefillEmail = localStorage.getItem('celey:concierge:email') || '';
                  } catch (_) { /* ignore */ }
                  const qs = new URLSearchParams({ mode: 'signup' });
                  if (prefillEmail) qs.set('email', prefillEmail);
                  if (token) qs.set('t', token);
                  navigate(`/auth?${qs.toString()}`);
                }}
                data-testid="concierge-submitted-signup"
              >
                Create an account to track this
              </Button>
            </div>
            {/* B-SEC-6.25 — existing Celey customers must have a clear
                login path. The share-token flows through /auth so the
                server-side claim runs after successful login. */}
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => {
                  const qs = new URLSearchParams({ mode: 'login', intent: 'claim' });
                  if (token) qs.set('t', token);
                  navigate(`/auth?${qs.toString()}`);
                }}
                className="text-sm text-gray-500 hover:text-emerald-700 underline underline-offset-2"
                data-testid="concierge-submitted-login-link"
              >
                Already have a Celey account? Log in to connect this request
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConciergeSubmittedPage;
